import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, Router } from '@angular/router';
import { CollaborateurService } from '@shared/services/cabinet/collaborateur.service';
import { DossierService } from '@shared/services/cabinet/dossier.service';
import { ClientDossiersService } from '../client-dossiers.service';
import { dossierIdDeLaRoute, premierDossierAutorise, refusVersAccueil, retourConnexionUrl } from '../page-arrivee';
import { urlAccueil } from '../chemins';
import { aDroit, DroitRequis, estFacturationComplete, MSG_ECRAN_NON_AUTORISE, MSG_ENTREPRISE_NON_AUTORISEE } from '../droits';
import { UserType } from '@logaia_users/logaia-dto';
import { NotificationService } from '../../ui/notification/notification.service';

/**
 * Droits d'un client sur les écrans d'un dossier.
 * Sur une route, `data` peut porter :
 * - `droit` : un droit, ou une liste dont au moins un suffit ;
 * - `droitSelonType` : droit supplémentaire selon le paramètre `:type`
 *   (ex. balance client → _CLIENT), exigé EN PLUS de `droit`.
 * - `facturationComplete` : écran réservé à la facturation complète (core/droits estFacturationComplete), en plus de
 *   `droit` (ex. plans de facturation).
 *
 * Correctifs par rapport à l'ancien code :
 * - les droits vérifiés sont ceux du dossier de l'URL (`dossier/:id`), et non du dossier
 *   courant en sessionStorage : les gardes passent avant le resolver, le dossier courant
 *   est donc encore l'ancien (changement de dossier) ou vide (nouvel onglet) ;
 * - un client sans aucun droit sur le dossier de l'URL est refusé (avant : accès libre).
 * Collaborateur : inchangé (tout est autorisé ; en mode « asUser », droits de l'interlocuteur
 * s'ils sont connus).
 *
 * Utilisable en canActivate (route `dossier/:id`) et en canActivateChild (écrans).
 */
export const accesDossierGuard: CanActivateChildFn = (childRoute: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const dossierService = inject(DossierService);
  const collaborateurService = inject(CollaborateurService);
  const clientDossiers = inject(ClientDossiersService);

  // Mode « en tant que » : fonction collaborateur, ignorée si l'accès collaborateur est inactif (constante écrite ici :
  // code écarté du build « client seul ») et pour un client. Correctif (revue du 23/10) : un client qui ajoutait
  // ?asUser= passait en mode client « collaborateur » et la garde le laissait entrer dans un dossier qui n'est pas à lui.
  const asUser =
    LOGAIA_COLLAB_BUILD && collaborateurService.user.userType === UserType.Collab
      ? childRoute.queryParamMap.get('asUser')
      : null;
  if (LOGAIA_COLLAB_BUILD && asUser) {
    const interlocuteur = dossierService
      .getDossierAccessRights()
      .find((i) => i.interlocuteurId === +asUser);
    if (interlocuteur) {
      collaborateurService.forceClientMode = true;
      collaborateurService.asUser = interlocuteur.interlocuteurId;
    }
  }

  if (!collaborateurService.isClient && !asUser) {
    return true;
  }
  // Collaborateur en mode client : même règle que l'ancien code (sans droits connus, accès libre).
  // Type réel de l'utilisateur (un client n'est jamais « collaborateur en mode client »).
  const collabEnModeClient = LOGAIA_COLLAB_BUILD && collaborateurService.user.userType === UserType.Collab;

  const id = dossierIdDeLaRoute(childRoute);
  const access = id ? clientDossiers.accesPour(id) : undefined;
  if (!access) {
    if (collabEnModeClient) {
      return true;
    }
    // Dossier non autorisé : message, puis dossier en cours (on ne change pas de dossier sans l'avoir demandé),
    // à défaut premier dossier autorisé, sinon retour à la connexion.
    inject(NotificationService).erreur(MSG_ENTREPRISE_NON_AUTORISEE);
    const courant = dossierService.dossierId;
    const retour = courant && courant !== id && clientDossiers.accesPour(courant) ? courant : premierDossierAutorise()?.id;
    if (retour && retour !== id) {
      return router.parseUrl(urlAccueil(retour));
    }
    return retourConnexionUrl();
  }

  const requis = droitsRequis(childRoute);
  const facturation = !childRoute.data['facturationComplete'] || estFacturationComplete(access);
  if (requis.every((d) => aDroit(access, d)) && facturation) {
    return true;
  }
  // Écran non autorisé (lien ou URL saisie) : message, puis accueil du dossier (jamais de retour silencieux).
  inject(NotificationService).erreur(MSG_ECRAN_NON_AUTORISE);
  return refusVersAccueil(childRoute);
};

/** Droits exigés par la route (chacun : au moins un des droits de la liste). */
export function droitsRequis(route: ActivatedRouteSnapshot): DroitRequis[] {
  const requis: DroitRequis[] = [];
  const droit = route.data['droit'] as DroitRequis | undefined;
  if (droit !== undefined) {
    requis.push(droit);
  }
  const selonType = route.data['droitSelonType'] as Record<string, DroitRequis> | undefined;
  const type = route.paramMap.get('type');
  if (selonType && type && selonType[type] !== undefined) {
    requis.push(selonType[type]);
  }
  return requis;
}
